import { useCallback } from "react";

import { FormInstance } from "antd/es/form/hooks/useForm";
import { IntlShape } from "react-intl";

import { checkInVisibilityToPlatformValue } from "@mapmycustomers/shared/enum/CheckInVisibility";
import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import ActivitiesSettings from "@app/scene/settings/type/ActivitiesSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { updateOrganization as updateOrganizationAction } from "@app/store/iam/actions";

const useHandleFinish = (
  form: FormInstance<ActivitiesSettings>,
  intl: IntlShape,
  updateOrganization: typeof updateOrganizationAction.request,
  unsetDirty: () => void,
  organizationId?: Organization["id"],
  activeCheckInSetting?: Setting,
  activeCheckInAccessSetting?: Setting,
  activityCompletionSetting?: Setting,
  activityVisibilitySetting?: Setting,
  checkInRadiusSetting?: Setting,
  checkInVerifiedVisibilitySetting?: Setting,
  crossObjectActivityVisibilitySetting?: Setting,
  nearbySuggestionActivityTypeSetting?: Setting
) =>
  useCallback(() => {
    const {
      activeCheckIn,
      activeCheckInAccess,
      activityCompletionStatus,
      activityVisibility,
      checkInRadius,
      checkInVerifiedVisibility,
      crossObjActivityVisibility,
      nearbySuggestionActivityType,
    } = form.getFieldsValue();
    if (organizationId) {
      updateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        },
        organization: { id: organizationId },
        settings: [
          activeCheckInSetting && isDefined(activeCheckIn)
            ? { ...activeCheckInSetting, value: activeCheckIn }
            : undefined,
          activeCheckInAccessSetting && isDefined(activeCheckInAccess)
            ? { ...activeCheckInAccessSetting, value: activeCheckInAccess }
            : undefined,
          activityCompletionSetting && activityCompletionStatus
            ? { ...activityCompletionSetting, value: activityCompletionStatus }
            : undefined,
          activityVisibilitySetting && activityVisibility
            ? { ...activityVisibilitySetting, value: activityVisibility }
            : undefined,
          checkInRadiusSetting && checkInRadius
            ? {
                ...checkInRadiusSetting,
                value: checkInRadius,
              }
            : undefined,
          checkInVerifiedVisibilitySetting && checkInVerifiedVisibility
            ? {
                ...checkInVerifiedVisibilitySetting,
                value: checkInVisibilityToPlatformValue[checkInVerifiedVisibility],
              }
            : undefined,
          crossObjectActivityVisibilitySetting && isDefined(crossObjActivityVisibility)
            ? { ...crossObjectActivityVisibilitySetting, value: crossObjActivityVisibility }
            : undefined,
          nearbySuggestionActivityTypeSetting && nearbySuggestionActivityType
            ? { ...nearbySuggestionActivityTypeSetting, value: nearbySuggestionActivityType }
            : undefined,
        ].filter(isDefined),
      });
    }
  }, [
    activeCheckInSetting,
    activeCheckInAccessSetting,
    activityCompletionSetting,
    activityVisibilitySetting,
    checkInRadiusSetting,
    checkInVerifiedVisibilitySetting,
    crossObjectActivityVisibilitySetting,
    form,
    intl,
    nearbySuggestionActivityTypeSetting,
    organizationId,
    unsetDirty,
    updateOrganization,
  ]);

export default useHandleFinish;
